import type { PDFDocument, PDFPage, PDFFont, PDFImage } from 'pdf-lib';
import { rgb } from 'pdf-lib';
import { hasPdfImagePayload, resolvePdfImageBytes } from '@src/modules/platform/pdf/preparePdfImage.ts';
import { yieldToUi } from '@src/modules/platform/pdf/pdfUiYield.ts';
import type { EntregaPdfDocument, EntregaPdfImage, EntregaPdfParticipante } from '../../domain/EntregaPdfModel.ts';
import {
  ENTREGA_PDF_DOC_ID,
  ENTREGA_PDF_ENCOUNTER_FIRMA_COL,
  ENTREGA_PDF_ENCOUNTER_FOTO_COL,
  ENTREGA_PDF_ENCOUNTER_BODY_BOTTOM,
  ENTREGA_PDF_ENCOUNTER_CONT_BODY_TOP,
  ENTREGA_PDF_ENCOUNTER_CONT_HEADER_TOP,
  ENTREGA_PDF_ENCOUNTER_FIRST_BODY_TOP,
  ENTREGA_PDF_ENCOUNTER_HEADER_FIELDS,
  ENTREGA_PDF_ENCOUNTER_HEADER_MASK,
  ENTREGA_PDF_ENCOUNTER_IMAGE_MIN,
  ENTREGA_PDF_ENCOUNTER_LEGAL_NOTICE_MASK,
  ENTREGA_PDF_ENCOUNTER_MIN_ROW_HEIGHT,
  ENTREGA_PDF_ENCOUNTER_TABLE_HEADER_MASK,
  entregaPdfAdjuntosStackRowHeight,
  ENTREGA_PDF_ENCOUNTER_TEMPLATE_ROW_BOTTOM,
  ENTREGA_PDF_MAP_BOX,
  ENTREGA_PDF_PAGE,
  ENTREGA_PDF_REMISION_FIELDS,
  ENTREGA_PDF_REMISION_SIGNATURES,
  ENTREGA_PDF_TABLE_COL_EDGES,
  ENTREGA_PDF_TEMPLATE_PAGE,
  ENTREGA_PDF_TOTALS_LINES,
  ENTREGA_PDF_UBICACION,
  entregaPdfDrawY,
  type EntregaPdfLayoutRect,
  type EntregaPdfTextField,
} from './entregaPdfTemplateLayout.ts';

/** Sizes chosen to sit on the Arial template baselines. */
const REMISION_SIZE = 10;
const HEADER_SIZE = 10;
const TABLE_FONT_SIZE = 9;
const TABLE_HEADER_FONT_SIZE = 6.5;
const TOTALS_SIZE = 11;
const SIGN_SIZE = 9;
const GRID_THICKNESS = 0.75;
const CELL_PITCH_PAD = 2;

const TABLE_COL_HEADERS = [
  'DOCUMENTO\nPARTICIPANTE\nY TIPO',
  'NOMBRE\nPARTICIPANTE',
  'PERSONA\nAUTORIZADA',
  'PRODUCTOS\nENTREGADOS',
  'PERSONA AUTORIZADA\nY PARENTESCO',
  'ADJUNTOS',
  'ASISTE A\nENCUENTRO\nEDUCATIVO',
  'ACEPTA DATOS\nSI/NO',
  'FIRMA',
  'REGISTRO\nFOTOGRÁFICO',
] as const;

function pdfY(topDown: number): number {
  return ENTREGA_PDF_PAGE.height - topDown;
}

function maskEncounterLegalNotice(page: PDFPage): void {
  const r = ENTREGA_PDF_ENCOUNTER_LEGAL_NOTICE_MASK;
  page.drawRectangle({ ...r, color: rgb(1, 1, 1), borderWidth: 0 });
}

function maskEncounterContinuationChrome(page: PDFPage): void {
  maskEncounterLegalNotice(page);
  page.drawRectangle({ ...ENTREGA_PDF_ENCOUNTER_HEADER_MASK, color: rgb(1, 1, 1), borderWidth: 0 });
  page.drawRectangle({ ...ENTREGA_PDF_ENCOUNTER_TABLE_HEADER_MASK, color: rgb(1, 1, 1), borderWidth: 0 });
}

type EncounterPageChunk = {
  participantes: EntregaPdfParticipante[];
  firstPage: boolean;
  rowHeight: number;
};

function encounterBodyTop(firstPage: boolean): number {
  return firstPage ? ENTREGA_PDF_ENCOUNTER_FIRST_BODY_TOP : ENTREGA_PDF_ENCOUNTER_CONT_BODY_TOP;
}

function encounterAvailableHeight(firstPage: boolean): number {
  return ENTREGA_PDF_ENCOUNTER_BODY_BOTTOM - encounterBodyTop(firstPage);
}

function adjuntosImageCount(p: EntregaPdfParticipante): number {
  return p.adjuntosImages.filter((img) => hasPdfImagePayload(img)).length;
}

/** Row height so each Adjuntos image (and Firma/Foto cell) can draw ≥ IMAGE_MIN. */
function desiredParticipanteRowHeight(p: EntregaPdfParticipante): number {
  return entregaPdfAdjuntosStackRowHeight(adjuntosImageCount(p));
}

function paginateEncounterParticipants(participantes: EntregaPdfParticipante[]): EncounterPageChunk[] {
  const chunks: EncounterPageChunk[] = [];
  if (participantes.length === 0) return chunks;

  let i = 0;
  let firstPage = true;
  // Keep full pre-printed legal on page 1; IMAGE_MIN rows need continuation body height.
  if (encounterAvailableHeight(true) < ENTREGA_PDF_ENCOUNTER_MIN_ROW_HEIGHT) {
    chunks.push({ participantes: [], firstPage: true, rowHeight: 0 });
    firstPage = false;
  }

  while (i < participantes.length) {
    const available = encounterAvailableHeight(firstPage);
    if (available < ENTREGA_PDF_ENCOUNTER_MIN_ROW_HEIGHT) {
      firstPage = false;
      continue;
    }
    const batch: EntregaPdfParticipante[] = [];
    while (i + batch.length < participantes.length) {
      const candidate = [...batch, participantes[i + batch.length]!];
      const need = Math.max(...candidate.map(desiredParticipanteRowHeight));
      if (candidate.length * need > available) break;
      batch.push(participantes[i + batch.length]!);
    }
    if (batch.length === 0) {
      batch.push(participantes[i]!);
      i += 1;
    } else {
      i += batch.length;
    }
    // Never shrink below per-adjunto IMAGE_MIN stack (batch already fits available).
    const rowHeight = Math.max(...batch.map(desiredParticipanteRowHeight));
    chunks.push({ participantes: batch, firstPage, rowHeight });
    firstPage = false;
  }
  return chunks;
}

function encounterRowBounds(bodyTop: number, rowHeight: number, index: number): { top: number; bottom: number } {
  const top = bodyTop + index * rowHeight;
  return { top, bottom: top + rowHeight };
}

async function embedPdfImage(doc: PDFDocument, image: EntregaPdfImage | null | undefined): Promise<PDFImage | null> {
  const resolved = await resolvePdfImageBytes(image);
  if (!resolved) return null;
  try {
    return resolved.format === 'PNG' ? doc.embedPng(resolved.bytes) : doc.embedJpg(resolved.bytes);
  } catch {
    return null;
  }
}

function drawImageFit(page: PDFPage, img: PDFImage, box: EntregaPdfLayoutRect): void {
  const scale = Math.min(box.width / img.width, box.height / img.height);
  const w = img.width * scale;
  const h = img.height * scale;
  page.drawImage(img, { x: box.x + (box.width - w) / 2, y: box.y + (box.height - h) / 2, width: w, height: h });
}

/** Fit image in box; when box ≥ IMAGE_MIN, drawn size is also ≥ IMAGE_MIN on the short side. */
function drawImageMinFit(page: PDFPage, img: PDFImage, box: EntregaPdfLayoutRect): void {
  const minSide = Math.min(box.width, box.height);
  if (minSide < ENTREGA_PDF_ENCOUNTER_IMAGE_MIN) {
    drawImageFit(page, img, box);
    return;
  }
  const scale = Math.min(box.width / img.width, box.height / img.height);
  let w = img.width * scale;
  let h = img.height * scale;
  const short = Math.min(w, h);
  if (short < ENTREGA_PDF_ENCOUNTER_IMAGE_MIN) {
    const boost = ENTREGA_PDF_ENCOUNTER_IMAGE_MIN / short;
    w = Math.min(box.width, w * boost);
    h = Math.min(box.height, h * boost);
  }
  page.drawImage(img, { x: box.x + (box.width - w) / 2, y: box.y + (box.height - h) / 2, width: w, height: h });
}

function wrapLine(text: string, font: PDFFont, size: number, maxWidth: number): string[] {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [''];
  const out: string[] = [];
  let line = words[0] ?? '';
  for (let i = 1; i < words.length; i++) {
    const next = `${line} ${words[i]}`;
    if (font.widthOfTextAtSize(next, size) <= maxWidth) line = next;
    else {
      out.push(line);
      line = words[i] ?? '';
    }
  }
  out.push(line);
  return out;
}

function wrapCellLines(text: string, font: PDFFont, size: number, maxWidth: number): string[] {
  return text.split('\n').flatMap((part) => wrapLine(part, font, size, maxWidth));
}

/** Single line drawn on the label baseline (header/label values that must not push down). */
function drawTextLine(page: PDFPage, text: string, x: number, yMin: number, font: PDFFont, size: number): void {
  page.drawText(text, { x, y: entregaPdfDrawY(yMin, size), size, font, color: rgb(0, 0, 0) });
}

/** Wrapped value block anchored at the label baseline (grows downward). */
function drawWrapped(
  page: PDFPage,
  field: EntregaPdfTextField,
  text: string,
  font: PDFFont,
  size: number,
): void {
  const lines = wrapCellLines(text, font, size, field.maxWidth);
  let yMin = field.yMin;
  for (const line of lines) {
    drawTextLine(page, line, field.x, yMin, font, size);
    yMin += size + CELL_PITCH_PAD;
  }
}

// ---------------------------------------------------------------------------
// Field maps
// ---------------------------------------------------------------------------
function remisionFieldMap(model: EntregaPdfDocument): Record<string, string> {
  const rem = model.remision;
  if (!rem) return {};
  const up = (s: string): string => s.trim().toUpperCase();
  return {
    puntoNombre: up(model.puntoNombre),
    direccion: up(model.direccion),
    programa: up(model.programa),
    itemNombre: up(model.itemNombre),
    nombreDespacha: up(rem.nombreDespacha),
    vehiculo: up(rem.vehiculo),
    fechaHora: up(rem.fechaHora),
    cantidadSolicitadasPaquetes: String(rem.cantidadSolicitadasPaquetes),
    cantidadEntregadasPaquetes: String(rem.cantidadEntregadasPaquetes),
    remanentePaquetes: String(rem.remanentePaquetes),
    cantidadSolicitadasBolsas: String(rem.cantidadSolicitadasBolsas),
    cantidadEntregadasBolsas: String(rem.cantidadEntregadasBolsas),
    remanenteBolsas: String(rem.remanenteBolsas),
    observaciones: rem.observaciones.trim() ? up(rem.observaciones) : '',
  };
}

function encounterHeaderMap(model: EntregaPdfDocument): Record<string, string> {
  return {
    puntoNombre: model.puntoNombre,
    comunaLabel: model.comunaLabel,
    fecha: model.fecha,
    itemNombre: model.itemNombre,
    nutricionistaLabel: model.nutricionistaLabel || '—',
    responsableLabel: model.responsableLabel || '—',
  };
}

function remisionPersonName(raw: string): string {
  const t = raw.trim();
  if (!t || /^NOMBRE DE QUIEN (ENTREGA|RECIBE|DESPACHA)$/i.test(t)) return '';
  return t.toUpperCase();
}

function cedulaDigits(raw: string): string {
  const t = raw.trim();
  if (!t || /cedula de quien/i.test(t)) return '';
  return t.replace(/^c\.c\.?\s*/i, '').replace(/\D/g, '');
}

function participanteCells(p: EntregaPdfParticipante): string[] {
  const items = p.productos.split(/,\s*/).filter(Boolean);
  const productos = items.map((item, i) => (i < items.length - 1 ? `${item},` : item)).join('\n');
  return [
    `${p.documento}\n${p.tipoDocumento}`,
    p.nombre,
    p.acudienteAutorizado,
    productos,
    p.personaAutorizada,
    p.adjuntos,
    p.encuentroEducativo,
    p.aceptaDatos,
    hasPdfImagePayload(p.firma) ? ' ' : '—',
    hasPdfImagePayload(p.foto) ? ' ' : '—',
  ];
}

// ---------------------------------------------------------------------------
// Table helpers (page 2)
// ---------------------------------------------------------------------------
const TABLE_CELL_ALIGN = ['left', 'left', 'center', 'center', 'center', 'center', 'center', 'center'] as const;

function cellBounds(col: number, row: { top: number; bottom: number }) {
  const left = ENTREGA_PDF_TABLE_COL_EDGES[col]!;
  const right = ENTREGA_PDF_TABLE_COL_EDGES[col + 1]!;
  return { left, right, width: right - left, height: row.bottom - row.top, top: row.top, bottom: row.bottom };
}

function drawCellText(
  page: PDFPage,
  text: string,
  col: number,
  row: { top: number; bottom: number },
  font: PDFFont,
  size: number,
  align: 'left' | 'center',
): void {
  const cell = cellBounds(col, row);
  const pad = 3;
  const maxWidth = cell.width - pad * 2;
  const lines = wrapCellLines(text, font, size, maxWidth);
  const pitch = size + CELL_PITCH_PAD;
  const blockH = lines.length * pitch;
  let yTop = cell.top + Math.max(pad, (cell.height - blockH) / 2);
  for (const line of lines) {
    const tw = font.widthOfTextAtSize(line, size);
    const x = align === 'center' ? cell.left + (cell.width - tw) / 2 : cell.left + pad;
    drawTextLine(page, line, x, yTop, font, size);
    yTop += pitch;
  }
}

/** Erase the template blank row line and draw grid for N participant rows. */
function drawEncounterTableGrid(page: PDFPage, bodyTop: number, rowHeight: number, rowCount: number): void {
  const left = ENTREGA_PDF_TABLE_COL_EDGES[0]!;
  const right = ENTREGA_PDF_TABLE_COL_EDGES[ENTREGA_PDF_TABLE_COL_EDGES.length - 1]!;
  const bodyBottom = bodyTop + rowHeight * rowCount;
  page.drawRectangle({
    x: left,
    y: pdfY(ENTREGA_PDF_ENCOUNTER_TEMPLATE_ROW_BOTTOM) - 1,
    width: right - left,
    height: 2,
    color: rgb(1, 1, 1),
  });
  const yTop = pdfY(bodyTop);
  const yBot = pdfY(bodyBottom);
  for (const x of ENTREGA_PDF_TABLE_COL_EDGES) {
    page.drawLine({ start: { x, y: yBot }, end: { x, y: yTop }, thickness: GRID_THICKNESS, color: rgb(0, 0, 0) });
  }
  for (let r = 0; r <= rowCount; r += 1) {
    const y = pdfY(bodyTop + r * rowHeight);
    page.drawLine({ start: { x: left, y }, end: { x: right, y }, thickness: GRID_THICKNESS, color: rgb(0, 0, 0) });
  }
}

const ADJUNTOS_COL = 5;

/** Stack 1–4 cédula scans; each slot is IMAGE_MIN×IMAGE_MIN (row height must allow it). */
function adjuntosImageSlots(row: { top: number; bottom: number }, count: number): EntregaPdfLayoutRect[] {
  const b = cellBounds(ADJUNTOS_COL, row);
  const n = Math.max(1, Math.min(count, 4));
  const pad = 2;
  const gap = 2;
  const size = ENTREGA_PDF_ENCOUNTER_IMAGE_MIN;
  const stackH = n * size + (n - 1) * gap;
  const innerTop = b.top + Math.max(pad, (b.height - stackH) / 2);
  const x = b.left + Math.max(pad, (b.width - size) / 2);
  return Array.from({ length: n }, (_, i) => ({
    x,
    y: pdfY(innerTop + i * (size + gap) + size),
    width: size,
    height: size,
  }));
}

function cellImageInset(col: number, row: { top: number; bottom: number }): EntregaPdfLayoutRect {
  const b = cellBounds(col, row);
  const pad = 2;
  const width = Math.max(ENTREGA_PDF_ENCOUNTER_IMAGE_MIN, b.width - pad * 2);
  const height = Math.max(ENTREGA_PDF_ENCOUNTER_IMAGE_MIN, b.height - pad * 2);
  const x = b.left + (b.width - width) / 2;
  const top = row.top + (b.height - height) / 2;
  return { x, y: pdfY(top + height), width, height };
}

async function drawParticipanteRow(
  outDoc: PDFDocument,
  page: PDFPage,
  p: EntregaPdfParticipante,
  font: PDFFont,
  row: { top: number; bottom: number },
): Promise<void> {
  const cells = participanteCells(p);
  const adjuntos = p.adjuntosImages.filter((img) => hasPdfImagePayload(img));
  for (let col = 0; col < TABLE_CELL_ALIGN.length; col++) {
    const cell = cells[col]!;
    if (cell === ' ') continue;
    if (col === ADJUNTOS_COL && adjuntos.length > 0) continue; // scans drawn as images instead of text
    drawCellText(page, cell, col, row, font, TABLE_FONT_SIZE, TABLE_CELL_ALIGN[col] ?? 'center');
  }
  const slots = adjuntosImageSlots(row, adjuntos.length);
  for (let i = 0; i < adjuntos.length && i < slots.length; i += 1) {
    const img = await embedPdfImage(outDoc, adjuntos[i]);
    if (img) drawImageMinFit(page, img, slots[i]!);
  }
  const firma = await embedPdfImage(outDoc, p.firma);
  if (firma) drawImageMinFit(page, firma, cellImageInset(ENTREGA_PDF_ENCOUNTER_FIRMA_COL, row));
  const foto = await embedPdfImage(outDoc, p.foto);
  if (foto) drawImageMinFit(page, foto, cellImageInset(ENTREGA_PDF_ENCOUNTER_FOTO_COL, row));
}

function drawEncounterTableHeaders(
  page: PDFPage,
  fontBold: PDFFont,
  headerTop: number,
  headerBottom: number,
  eraseTemplateHeader: boolean,
): void {
  if (eraseTemplateHeader) {
    page.drawRectangle({ ...ENTREGA_PDF_ENCOUNTER_TABLE_HEADER_MASK, color: rgb(1, 1, 1), borderWidth: 0 });
  }
  const left = ENTREGA_PDF_TABLE_COL_EDGES[0]!;
  const right = ENTREGA_PDF_TABLE_COL_EDGES[ENTREGA_PDF_TABLE_COL_EDGES.length - 1]!;
  for (const x of ENTREGA_PDF_TABLE_COL_EDGES) {
    page.drawLine({
      start: { x, y: pdfY(headerBottom) },
      end: { x, y: pdfY(headerTop) },
      thickness: GRID_THICKNESS,
      color: rgb(0, 0, 0),
    });
  }
  page.drawLine({
    start: { x: left, y: pdfY(headerTop) },
    end: { x: right, y: pdfY(headerTop) },
    thickness: GRID_THICKNESS,
    color: rgb(0, 0, 0),
  });
  page.drawLine({
    start: { x: left, y: pdfY(headerBottom) },
    end: { x: right, y: pdfY(headerBottom) },
    thickness: GRID_THICKNESS,
    color: rgb(0, 0, 0),
  });
  for (let col = 0; col < TABLE_COL_HEADERS.length; col += 1) {
    drawCellText(
      page,
      TABLE_COL_HEADERS[col]!,
      col,
      { top: headerTop, bottom: headerBottom },
      fontBold,
      TABLE_HEADER_FONT_SIZE,
      'center',
    );
  }
}

// ---------------------------------------------------------------------------
// Pages
// ---------------------------------------------------------------------------
async function drawRemisionPage(
  outDoc: PDFDocument,
  templateDoc: PDFDocument,
  model: EntregaPdfDocument,
  font: PDFFont,
): Promise<void> {
  const [tpl] = await outDoc.copyPages(templateDoc, [ENTREGA_PDF_TEMPLATE_PAGE.remision]);
  const page = outDoc.addPage(tpl);

  const values = remisionFieldMap(model);
  for (const field of ENTREGA_PDF_REMISION_FIELDS) {
    const text = values[field.key];
    if (text) drawWrapped(page, field, text, font, REMISION_SIZE);
  }

  const rem = model.remision;
  if (!rem) return;

  const drawSignature = async (
    slot: { image: EntregaPdfLayoutRect; center: number; nameYMin: number; ccX: number; ccYMin: number },
    image: EntregaPdfImage,
    name: string,
    cedula: string,
  ): Promise<void> => {
    const sig = await embedPdfImage(outDoc, image);
    if (sig) drawImageFit(page, sig, slot.image);
    const n = remisionPersonName(name);
    if (n) {
      const x = slot.center - font.widthOfTextAtSize(n, SIGN_SIZE) / 2;
      drawTextLine(page, n, x, slot.nameYMin, font, SIGN_SIZE);
    }
    const c = cedulaDigits(cedula);
    if (c) drawTextLine(page, c, slot.ccX, slot.ccYMin, font, SIGN_SIZE);
  };
  await drawSignature(ENTREGA_PDF_REMISION_SIGNATURES.entrega, rem.firmaEntrega, rem.nombreEntrega, rem.cedulaEntrega);
  await drawSignature(ENTREGA_PDF_REMISION_SIGNATURES.recibe, rem.firmaRecibe, rem.nombreRecibe, rem.cedulaRecibe);
}

async function drawEncounterPage(
  outDoc: PDFDocument,
  templateDoc: PDFDocument,
  model: EntregaPdfDocument,
  chunk: EncounterPageChunk,
  font: PDFFont,
  fontBold: PDFFont,
): Promise<void> {
  const [tpl] = await outDoc.copyPages(templateDoc, [ENTREGA_PDF_TEMPLATE_PAGE.encuentro]);
  const page = outDoc.addPage(tpl);

  if (chunk.firstPage) {
    const header = encounterHeaderMap(model);
    for (const field of ENTREGA_PDF_ENCOUNTER_HEADER_FIELDS) {
      drawTextLine(page, header[field.key] ?? '—', field.x, field.yMin, fontBold, HEADER_SIZE);
    }
  } else {
    maskEncounterContinuationChrome(page);
  }

  if (chunk.participantes.length === 0) {
    // Full legal stays; hide empty pre-printed table (rows go to continuation).
    page.drawRectangle({ ...ENTREGA_PDF_ENCOUNTER_TABLE_HEADER_MASK, color: rgb(1, 1, 1), borderWidth: 0 });
    const left = ENTREGA_PDF_TABLE_COL_EDGES[0]!;
    const right = ENTREGA_PDF_TABLE_COL_EDGES[ENTREGA_PDF_TABLE_COL_EDGES.length - 1]!;
    page.drawRectangle({
      x: left,
      y: pdfY(ENTREGA_PDF_ENCOUNTER_BODY_BOTTOM),
      width: right - left,
      height: ENTREGA_PDF_ENCOUNTER_BODY_BOTTOM - ENTREGA_PDF_ENCOUNTER_FIRST_BODY_TOP + 2,
      color: rgb(1, 1, 1),
      borderWidth: 0,
    });
    return;
  }

  if (chunk.firstPage) {
    drawEncounterTableHeaders(
      page,
      fontBold,
      440,
      ENTREGA_PDF_ENCOUNTER_FIRST_BODY_TOP,
      true,
    );
  } else {
    drawEncounterTableHeaders(
      page,
      fontBold,
      ENTREGA_PDF_ENCOUNTER_CONT_HEADER_TOP,
      ENTREGA_PDF_ENCOUNTER_CONT_BODY_TOP,
      false,
    );
  }

  const bodyTop = encounterBodyTop(chunk.firstPage);
  const rowHeight = chunk.rowHeight;
  drawEncounterTableGrid(page, bodyTop, rowHeight, chunk.participantes.length);
  for (let i = 0; i < chunk.participantes.length; i += 1) {
    await drawParticipanteRow(outDoc, page, chunk.participantes[i]!, font, encounterRowBounds(bodyTop, rowHeight, i));
    await yieldToUi();
  }
}

async function drawTotalsPage(
  outDoc: PDFDocument,
  templateDoc: PDFDocument,
  model: EntregaPdfDocument,
  font: PDFFont,
  fontBold: PDFFont,
): Promise<void> {
  const [tpl] = await outDoc.copyPages(templateDoc, [ENTREGA_PDF_TEMPLATE_PAGE.totals]);
  const page = outDoc.addPage(tpl);

  const totals: Record<string, number> = {
    paquetesEntregados: model.totales.paquetesEntregados,
    capacitados: model.totales.capacitados,
    bolsasReutilizables: model.totales.bolsasReutilizables,
  };
  for (const line of ENTREGA_PDF_TOTALS_LINES) {
    drawTextLine(page, String(totals[line.key] ?? 0), line.x, line.yMin, fontBold, TOTALS_SIZE);
  }

  const mapImg = await embedPdfImage(outDoc, model.ubicacionMapa);
  if (mapImg) {
    drawImageFit(page, mapImg, ENTREGA_PDF_MAP_BOX);
  } else if (model.ubicacionLat != null && model.ubicacionLng != null) {
    const coords = `${model.ubicacionLat.toFixed(6)}, ${model.ubicacionLng.toFixed(6)}`;
    drawTextLine(page, coords, ENTREGA_PDF_UBICACION.x, ENTREGA_PDF_UBICACION.yMin, font, TOTALS_SIZE);
  }

  drawTextLine(page, model.documentoId, ENTREGA_PDF_DOC_ID.x, ENTREGA_PDF_DOC_ID.yMin, font, SIGN_SIZE);
}

export async function renderEntregaPdfFromTemplate(
  templateBytes: Uint8Array,
  model: EntregaPdfDocument,
): Promise<Uint8Array> {
  const { PDFDocument, StandardFonts } = await import('pdf-lib');
  const templateDoc = await PDFDocument.load(templateBytes);
  const outDoc = await PDFDocument.create();
  const font = await outDoc.embedFont(StandardFonts.Helvetica);
  const fontBold = await outDoc.embedFont(StandardFonts.HelveticaBold);

  if (model.remision) {
    await drawRemisionPage(outDoc, templateDoc, model, font);
    await yieldToUi();
  }

  for (const chunk of paginateEncounterParticipants(model.participantes)) {
    await drawEncounterPage(outDoc, templateDoc, model, chunk, font, fontBold);
    await yieldToUi();
  }

  if (model.participantes.length > 0 || model.remision) {
    await drawTotalsPage(outDoc, templateDoc, model, font, fontBold);
  }

  await yieldToUi();
  return outDoc.save();
}
